/**
 * Gets closest scrollable parent for an element or itself.
 *
 * Falls back to document.body if no scrollable parent is found.
 */
export function getClosestScrollableParent(
  element: HTMLElement,
  includeHidden?: boolean,
): HTMLElement {
  let style = getComputedStyle(element);
  const excludeStaticParent = style.position === "absolute";
  const overflowRegex = includeHidden
    ? /(auto|scroll|hidden)/
    : /(auto|scroll)/;

  if (style.position === "fixed") return document.body;
  let parent: HTMLElement | null = element;

  do {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === "static") {
      continue;
    }
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX))
      return parent;

    if (parent === parent.parentElement) break;

    parent = parent.parentElement ?? null;
  } while (parent !== null);

  return document.body;
}

/**
 * Check if a value is an object. This is a type guard.
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function waitFor(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Like {@link waitFor}, but only counts down while the document is visible.
 *
 * A backgrounded tab throttles/pauses `setTimeout`; a long reveal timer
 * (e.g. 6s/12s) started before backgrounding then fires the instant the tab
 * is foregrounded again, and several such timers batch-fire in one tick —
 * which interrupts in-flight quiz transition cascades and drains the queue in
 * the wrong order. Scheduling only while visible (and only for the remaining
 * visible time) prevents the batch-fire: at most one timer is pending, and
 * it is (re)scheduled only when the tab is actually visible.
 */
export function waitForVisible(ms: number): Promise<void> {
  if (typeof document === "undefined") {
    return waitFor(ms) as Promise<void>;
  }
  return new Promise<void>((resolve) => {
    let remaining = ms;
    let startedAt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clear = () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const finish = () => {
      clear();
      document.removeEventListener("visibilitychange", onVisibility);
      resolve();
    };
    const start = () => {
      if (timer !== null || document.visibilityState === "hidden") return;
      startedAt = Date.now();
      timer = setTimeout(finish, remaining);
    };
    const pause = () => {
      if (timer === null) return;
      clear();
      remaining = Math.max(0, remaining - (Date.now() - startedAt));
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") pause();
      else start();
    };

    document.addEventListener("visibilitychange", onVisibility);
    start();
  });
}

export interface ExponentialBackoffOptions {
  baseDelayMs: number;
  maxDelayMs: number;
  jitterPercentage?: number;
}

export function calculateExponentialBackoff(
  attemptNumber: number,
  options: ExponentialBackoffOptions,
): number {
  const { baseDelayMs, maxDelayMs, jitterPercentage = 0.2 } = options;

  const exponentialDelay = Math.min(
    baseDelayMs * Math.pow(2, attemptNumber),
    maxDelayMs,
  );

  const jitterAmount =
    exponentialDelay * jitterPercentage * (Math.random() - 0.5);

  return exponentialDelay + jitterAmount;
}

export function idify(str: string) {
  return "el-" + str.toLowerCase();
}

export function getUrlOrAbsoluteUrlFromOrigin<S extends string | undefined>(
  url: S,
): S {
  if (url === undefined) return url;
  return new URL(url, window.location.origin).href as S;
}

declare global {
  interface Window {
    IF_THE_WEBSITE_IS_NOT_RUNNING_YET_CALL_ADAM: boolean;
    IS_WP_FLYING_HIGH: boolean;
  }
}

export async function detectAdBlock(): Promise<boolean> {
  const checkScriptsWithConstants =
    window.IF_THE_WEBSITE_IS_NOT_RUNNING_YET_CALL_ADAM === true &&
    window.IS_WP_FLYING_HIGH === true;

  const checkFakeAd = !!document.getElementById("deVosLIpjuqz");

  return !(checkScriptsWithConstants && checkFakeAd);
}
